import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Flame, Flower, Flower2, Heart, BookOpen, Church, PencilLine, Loader2, Sparkles } from "lucide-react";
import TransliterateInput from "@/components/TransliterateInput";

const TRIBUTES = [
  { key: "candle",         label: "Light a candle",   Icon: Flame,    color: "text-amber-500",  emoji: "🕯" },
  { key: "rose",           label: "Leave a rose",     Icon: Flower,   color: "text-rose-600",   emoji: "🌹" },
  { key: "carnation",      label: "Leave a carnation",Icon: Flower,   color: "text-pink-500",   emoji: "🌷" },
  { key: "cherry_blossom", label: "Cherry blossom",   Icon: Flower,   color: "text-fuchsia-500",emoji: "🌸" },
  { key: "bouquet",        label: "Send a bouquet",   Icon: Flower2,  color: "text-emerald-500",emoji: "💐" },
  { key: "dove",           label: "Release a dove",   Icon: Sparkles, color: "text-sky-500",    emoji: "🕊" },
  { key: "prayer",         label: "Offer a prayer",   Icon: Church,   color: "text-indigo-500", emoji: "🙏" },
];

/**
 * Virtual-tributes widget for a memorial detail page.
 * Displays six one-tap tribute buttons with live counters, plus an
 * optional note field, plus the latest N tributes as a scroll strip.
 *
 * Props:
 *   slug   : memorial slug or id
 *   user   : current auth user (may be null; anonymous tributes allowed)
 */
export default function TributeWidget({ slug, user }) {
  const [counts, setCounts] = useState({});
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(null);
  const [openNote, setOpenNote] = useState(null);   // kind currently in "add note" mode
  const [note, setNote] = useState("");
  const [authorName, setAuthorName] = useState("");

  const load = () => {
    http.get(`/memorials/${slug}/tributes`, { params: { limit: 24 } })
      .then((r) => { setCounts(r.data?.counts || {}); setRecent(r.data?.items || []); })
      .catch(() => {});
  };
  useEffect(load, [slug]);

  const post = async (kind) => {
    setBusy(kind);
    try {
      await http.post(`/memorials/${slug}/tributes`, {
        kind,
        note: note.trim() || undefined,
        author_name: (authorName || user?.name || "").trim() || undefined,
      });
      toast.success("Your tribute has been added.");
      setOpenNote(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save tribute");
    } finally { setBusy(null); }
  };

  return (
    <div className="wofy-card" data-testid="mem-tribute-widget">
      <div className="text-sm font-mono uppercase tracking-wider mb-3 font-semibold" style={{ color: "var(--wofy-primary)" }}>Leave a tribute</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {TRIBUTES.map(({ key, label, emoji, color }) => {
          const count = counts[key] || 0;
          const isOpen = openNote === key;
          return (
            <button
              key={key}
              type="button"
              disabled={!!busy}
              onClick={() => setOpenNote(isOpen ? null : key)}
              className={`rounded-xl border px-3 py-3 text-left flex items-center gap-3 transition hover:shadow-md hover:-translate-y-[1px] ${isOpen ? "ring-2 ring-offset-1" : ""}`}
              style={{
                borderColor: "var(--wofy-border)",
                background: isOpen ? "#FFF3D6" : "#FFFDF8",
                // Use color-mix via CSS custom prop for the focus ring
                ringColor: "var(--wofy-primary)",
              }}
              data-testid={`mem-tribute-btn-${key}`}
              aria-label={label}
              title={label}
            >
              <span aria-hidden="true" className={`text-3xl leading-none ${color}`}>{emoji}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate" title={label}>{label}</div>
                <div className="text-xs opacity-70 font-mono mt-0.5">{count.toLocaleString()} {count === 1 ? "tribute" : "tributes"}</div>
              </div>
            </button>
          );
        })}
      </div>

      {openNote && (
        <div className="mt-3 rounded-xl border p-3 space-y-2" style={{ borderColor: "var(--wofy-border)" }} data-testid={`mem-tribute-note-${openNote}`}>
          {!user && (
            <input
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="Your name (optional)"
              className="w-full rounded-lg border px-3 py-2 text-sm bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="mem-tribute-author"
            />
          )}
          <TransliterateInput
            value={note}
            onChange={setNote}
            placeholder={`Add a note with your ${openNote}… (optional). Switch language chip to type in Malayalam, Hindi, Tamil…`}
            rows={2}
            data-testid="mem-tribute-body"
          />
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => { setOpenNote(null); setNote(""); }} className="text-xs opacity-70">Cancel</button>
            <button type="button" onClick={() => post(openNote)} disabled={busy === openNote} className="wofy-btn-primary text-sm" data-testid="mem-tribute-submit">
              {busy === openNote ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</> : "Send"}
            </button>
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">Recent tributes</div>
          <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
            {recent.slice(0, 12).map((t) => {
              const Icon = (TRIBUTES.find((x) => x.key === t.kind) || TRIBUTES[0]).Icon;
              return (
                <div key={t.id} className="text-[12px] flex items-start gap-2" data-testid={`mem-tribute-recent-${t.id}`}>
                  <Icon className="w-3 h-3 mt-0.5 opacity-70" />
                  <div className="min-w-0">
                    <span className="font-medium">{t.author_name || "Anonymous"}</span>
                    {t.note && <span className="opacity-80"> — {t.note}</span>}
                    <div className="text-[10px] font-mono opacity-50">{new Date(t.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
